import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  LogOut, Loader2, MapPin, Phone, Mail, Briefcase, Users, Calendar, ShieldCheck,
  FileText, Hammer, Star, BadgeCheck, Clock, Ban, Send, CheckCircle2, UserPlus,
  Printer, CreditCard, ClipboardEdit,
} from "lucide-react";
import InstallAppButton from "../components/InstallAppButton";
import WorkmenDrawer from "../components/WorkmenDrawer";
import WorkmenList from "../components/WorkmenList";
import PortalEventNotifier from "../components/PortalEventNotifier";
import { printScreeningForm, printIdentityCard, printFormXI } from "../components/WorkmenPrintables";
import { contractorToWorkman } from "../lib/contractorPrintAdapter";
import PayrollScreenFormDrawer from "../components/PayrollScreenFormDrawer";
import { DigitalIdCard } from "../components/DigitalIdCard";

const NCC_LOGO = "/ncc-logo.png";
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function photoSrc(photo) {
  if (!photo?.path) return null;
  return `${API}/files/${photo.path}`;
}

export default function ContractorPortalPage() {
  const nav = useNavigate();
  const [contractor, setContractor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [signaling, setSignaling] = useState(false);
  const [readyNos, setReadyNos] = useState("");
  const [readyDate, setReadyDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [workmenOpen, setWorkmenOpen] = useState(false);
  const [workmen, setWorkmen] = useState([]);
  const [siteMasters, setSiteMasters] = useState([]);
  const [editingWorkmanId, setEditingWorkmanId] = useState(null);
  const [payrollScreenOpen, setPayrollScreenOpen] = useState(false);
  const workmenCount = workmen.length;

  const token = useMemo(() => localStorage.getItem("contractor_token"), []);
  const authHeader = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  useEffect(() => {
    let cancelled = false;
    if (!token) { nav("/"); return; }
    (async () => {
      try {
        const r = await axios.get(`${API}/contractor-auth/me`, { headers: authHeader });
        if (!cancelled) {
          setContractor(r.data);
          if (r.data?.ready_nos) setReadyNos(String(r.data.ready_nos));
          if (r.data?.ready_date) setReadyDate(r.data.ready_date);
        }
        if (!r.data?.is_payroll) {
          try {
            const w = await axios.get(`${API}/contractor-auth/workmen`, { headers: authHeader });
            if (!cancelled) setWorkmen(w.data || []);
          } catch { /* ignore */ }
        }
        // Site masters needed for both: workmen roster (labour) AND payroll ID Card client logo lookup
        try {
          const sm = await axios.get(`${API}/masters/ncc_site`);
          if (!cancelled) setSiteMasters(sm.data || []);
        } catch { /* ignore */ }
      } catch (err) {
        if (cancelled) return;
        if (err?.response?.status === 401) {
          localStorage.removeItem("contractor_token");
          localStorage.removeItem("contractor_id");
          nav("/");
          return;
        }
        setError(err?.response?.data?.detail || "Could not load profile");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [nav, token, authHeader]);

  function logout() {
    localStorage.removeItem("contractor_token");
    localStorage.removeItem("contractor_id");
    nav("/");
  }

  // ----- Payroll Manpower printables (Screening Form + Identity Card) -----
  const siteFor = (name) => (siteMasters || []).find((s) => s.name === name) || null;

  async function ensurePayrollIdCard() {
    if (!contractor) return null;
    if (contractor.id_card_no) return contractor;
    if (!contractor.ncc_site_name) {
      toast.error("Set 'NCC Site' before printing the Identity Card.");
      return null;
    }
    try {
      const r = await axios.post(
        `${API}/contractor-auth/issue-id-card`,
        {},
        { headers: authHeader },
      );
      const next = {
        ...contractor,
        id_card_no: r.data.id_card_no,
        id_card_issued_at: r.data.id_card_issued_at,
      };
      setContractor(next);
      return next;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not issue ID No.");
      return null;
    }
  }

  function handlePrintScreening() {
    if (!contractor) return;
    const w = contractorToWorkman(contractor);
    printScreeningForm(w, contractor);
  }

  async function handlePrintIdCard() {
    const enrichedContractor = await ensurePayrollIdCard();
    if (!enrichedContractor) return;
    const w = contractorToWorkman(enrichedContractor);
    printIdentityCard(w, enrichedContractor, siteFor(enrichedContractor.ncc_site_name));
  }

  if (loading) {
    return (
      <div className="grid min-h-screen place-items-center bg-[#f5f7fc]">
        <Loader2 className="h-8 w-8 animate-spin text-[#1e3a8a]" />
      </div>
    );
  }

  if (error || !contractor) {
    return (
      <div className="grid min-h-screen place-items-center bg-[#f5f7fc] px-4">
        <div className="max-w-md rounded-2xl border border-slate-200 bg-white p-6 text-center shadow-lg">
          <p className="text-sm text-red-700">{error || "Profile not found"}</p>
          <button onClick={logout} className="mt-4 rounded-lg bg-[#1e3a8a] px-4 py-2 text-sm font-bold tracking-wider text-white">BACK TO LOGIN</button>
        </div>
      </div>
    );
  }

  const c = contractor;
  const isPayroll = !!c.is_payroll;
  const signalType = isPayroll ? "join" : "mobilize";
  const signalLabel = isPayroll ? "Ready to Join" : "Ready to Mobilize";
  const alreadySignaled = c.ready_signal_type === signalType && !c.ready_signal_seen;
  const statusPill = (
    c.status === "approved" ? { Icon: BadgeCheck, label: "APPROVED · LIVE", cls: "bg-emerald-100 text-emerald-800" }
    : c.status === "rejected" ? { Icon: Ban, label: "REJECTED", cls: "bg-red-100 text-red-800" }
    : { Icon: Clock, label: "PENDING APPROVAL", cls: "bg-amber-100 text-amber-800" }
  );

  async function signalReady() {
    if (!readyDate) {
      toast.error("Please pick a Ready Date");
      return;
    }
    if (!isPayroll) {
      const n = Number(readyNos);
      if (!n || n < 1) {
        toast.error("Please enter the Nos. of workers ready to mobilize");
        return;
      }
    }
    setSignaling(true);
    try {
      const body = { type: signalType, date: readyDate };
      if (!isPayroll) body.nos = Number(readyNos);
      await axios.post(`${API}/contractor-auth/signal-ready`, body, { headers: authHeader });
      toast.success(isPayroll ? "Admin notified — you're ready to join." : "Admin notified — you're ready to mobilize.");
      setContractor({
        ...c,
        ready_signal_type: signalType,
        ready_signal_seen: false,
        ready_signaled_at: new Date().toISOString(),
        ready_nos: !isPayroll ? Number(readyNos) : null,
        ready_date: readyDate,
      });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not send signal");
    } finally {
      setSignaling(false);
    }
  }

  return (
    <div className="min-h-screen bg-[#f5f7fc]">
      {/* Realtime approval / rejection notifier — polls every 25s, shows
          toast + acknowledgement modal for any Pay-Roster or Workmen Roster
          status change that landed since the contractor last checked. */}
      <PortalEventNotifier
        contractor={c}
        workmen={workmen}
        authHeader={authHeader}
        onContractor={setContractor}
        onWorkmen={setWorkmen}
      />
      <header className="sticky top-0 z-40 border-b-2 border-ncc-gold bg-ncc-navy">
        <div className="mx-auto flex max-w-5xl items-center justify-between px-4 py-3 md:px-8">
          <div className="flex h-12 items-center rounded-md bg-white px-2 py-1">
            <img src={NCC_LOGO} alt="NCC-SWMS · MANPOWER DATABANK" className="h-full w-auto object-contain" />
          </div>
          <div className="flex items-center gap-2">
            <InstallAppButton variant="ghost" size="sm" label="Install" />
            <button
              onClick={logout}
              data-testid="portal-logout"
              className="inline-flex items-center gap-2 rounded-md bg-ncc-gold px-3 py-2 industrial-label text-ncc-navy transition-all hover:-translate-y-0.5 hover:bg-ncc-gold2"
            >
              <LogOut className="h-4 w-4" /> Logout
            </button>
          </div>
        </div>
        <div className="h-1 bg-gradient-to-r from-ncc-navy via-ncc-gold to-ncc-gold2" />
      </header>

      <main className="mx-auto max-w-5xl px-4 py-8 md:px-8">
        <div className="mb-6 flex flex-wrap items-end justify-between gap-3">
          <div>
            <div className="industrial-label text-ncc-gold2">// My Profile</div>
            <h1 className="mt-1 font-heading text-3xl font-black uppercase tracking-tight text-slate-900 md:text-4xl">
              Welcome, {c.name}
            </h1>
          </div>
        </div>

        <div className={`flex items-center gap-3 rounded-xl border border-slate-200 px-4 py-3 ${statusPill.cls}`} data-testid="portal-status">
          <statusPill.Icon className="h-5 w-5" />
          <div className="text-sm font-bold tracking-wider">{statusPill.label}</div>
        </div>

        {c.status === "approved" && (
          <div className="mt-4 rounded-xl border-2 border-ncc-navy bg-white p-4 shadow-sm" data-testid="portal-ready-form">
            <div className="industrial-label mb-3 text-ncc-navy">
              {isPayroll ? "Ready to Join" : "Ready to Mobilize"}
            </div>
            {alreadySignaled && (
              <div className="mb-3 rounded-md bg-emerald-50 px-3 py-2 text-xs font-semibold text-emerald-800" data-testid="portal-ready-acknowledged">
                <CheckCircle2 className="mr-1 inline h-4 w-4 -translate-y-0.5" />
                ADMIN NOTIFIED · You can update Nos / Date and resubmit anytime.
              </div>
            )}
            <div className={`grid gap-3 ${isPayroll ? "md:grid-cols-2" : "md:grid-cols-3"}`}>
              {!isPayroll && (
                <label className="block">
                  <div className="industrial-label mb-1 text-[10px] text-slate-500">Nos. of Workers</div>
                  <input
                    type="number"
                    min="1"
                    inputMode="numeric"
                    value={readyNos}
                    onChange={(e) => setReadyNos(e.target.value)}
                    placeholder="e.g. 25"
                    data-testid="portal-ready-nos"
                    className="w-full rounded-md border-2 border-slate-300 bg-slate-50 px-3 py-2 font-mono text-sm focus:border-ncc-navy focus:outline-none"
                  />
                </label>
              )}
              <label className="block">
                <div className="industrial-label mb-1 text-[10px] text-slate-500">Ready Date</div>
                <input
                  type="date"
                  value={readyDate}
                  onChange={(e) => setReadyDate(e.target.value)}
                  data-testid="portal-ready-date"
                  className="w-full rounded-md border-2 border-slate-300 bg-slate-50 px-3 py-2 font-mono text-sm focus:border-ncc-navy focus:outline-none"
                />
              </label>
              <div className="flex items-end">
                <button
                  type="button"
                  onClick={signalReady}
                  disabled={signaling}
                  data-testid="portal-ready-btn"
                  className="inline-flex w-full items-center justify-center gap-2 rounded-md border-2 border-ncc-navy bg-ncc-gold px-5 py-2.5 industrial-label text-ncc-navy transition-all hover:-translate-y-0.5 hover:shadow-brutal-sm disabled:opacity-70 disabled:hover:translate-y-0 disabled:hover:shadow-none"
                >
                  {signaling ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                  {signaling ? "SUBMITTING…" : `SUBMIT · ${signalLabel.toUpperCase()}`}
                </button>
              </div>
            </div>
            {c.ready_nos || c.ready_date ? (
              <div className="mt-3 border-t border-slate-200 pt-2 font-mono text-xs text-slate-600" data-testid="portal-ready-last">
                Last signaled: {c.ready_nos ? <b>{c.ready_nos} workers</b> : null}{c.ready_nos && c.ready_date ? " · " : ""}{c.ready_date ? <b>{c.ready_date}</b> : null}
              </div>
            ) : null}
          </div>
        )}

        <div className="mt-6 grid gap-6 md:grid-cols-3">
          <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm md:col-span-1">
            <div className="grid place-items-center">
              <div className="grid h-32 w-32 place-items-center overflow-hidden rounded-full border-4 border-ncc-gold/40 bg-[#eef2ff]">
                {c.photo?.path ? (
                  <img src={photoSrc(c.photo)} alt={c.name} className="h-full w-full object-cover" />
                ) : (
                  <Users className="h-14 w-14 text-[#1e3a8a]" />
                )}
              </div>
              <div className="mt-3 font-heading text-xl font-extrabold text-slate-900">{c.name}</div>
              <div className="text-xs font-semibold tracking-wider text-slate-500">
                {isPayroll ? ((c.trades && c.trades[0]) || "Payroll Manpower") : c.contractor_type}
              </div>
              {c.current_status === "NCCL" && (
                <div className="mt-2 rounded-md border border-ncc-navy bg-ncc-tint px-2 py-0.5 text-xs font-bold text-ncc-navy">
                  NCCL · {c.ncc_site_name || "—"}
                </div>
              )}
            </div>
            <div className="mt-4 space-y-2 text-sm text-slate-700">
              <Row icon={<Phone className="h-4 w-4 text-[#1e3a8a]" />} value={c.contact_phone} />
              {c.contact_email && <Row icon={<Mail className="h-4 w-4 text-[#1e3a8a]" />} value={c.contact_email} />}
              <Row icon={<MapPin className="h-4 w-4 text-[#1e3a8a]" />} value={c.location} />
              {c.avg_rating > 0 && (
                <Row icon={<Star className="h-4 w-4 text-ncc-gold2 fill-ncc-gold2" />} value={`${c.avg_rating.toFixed(1)} (${c.review_count} reviews)`} />
              )}
            </div>

            {/* Google Play install QR */}
            <div
              data-testid="portal-install-qr"
              className="mt-5 flex items-center gap-3 rounded-xl border-2 border-dashed border-ncc-navy bg-ncc-tint/40 p-3"
            >
              <img
                src={`https://api.qrserver.com/v1/create-qr-code/?size=160x160&margin=2&data=${encodeURIComponent("https://play.google.com/store/apps/details?id=com.ncc.swms.manpowerdatabank")}`}
                alt="Install NCC-SWMS Android app"
                width={88}
                height={88}
                className="rounded-md bg-white p-1.5 ring-1 ring-slate-200"
                data-testid="portal-install-qr-img"
              />
              <div className="min-w-0 flex-1">
                <div className="industrial-label text-[10px] text-ncc-navy">// Install Android App</div>
                <div className="mt-0.5 font-heading text-sm font-extrabold text-slate-900">Scan to Install</div>
                <div className="mt-0.5 font-mono text-[10px] leading-snug text-slate-600">
                  Open Camera → point at QR → tap the Play Store link
                </div>
                <a
                  href="https://play.google.com/store/apps/details?id=com.ncc.swms.manpowerdatabank"
                  target="_blank"
                  rel="noreferrer"
                  data-testid="portal-install-qr-link"
                  className="mt-1.5 inline-block text-[10px] font-bold uppercase tracking-wider text-ncc-navy underline"
                >
                  Or tap to open ↗
                </a>
              </div>
            </div>
          </div>

          <div className="space-y-6 md:col-span-2">
            {!isPayroll && (
              <Card title="Trades & Crew" icon={<Hammer className="h-4 w-4 text-ncc-gold2" />}>
                <div className="flex flex-wrap gap-2">
                  {(c.trades || []).map((t) => {
                    const n = c.trade_counts?.[t];
                    return (
                      <span key={t} className="rounded-md border border-ncc-navy bg-ncc-navy px-3 py-1 text-xs font-bold tracking-wider text-white">
                        {t}{n ? ` · ${n}` : ""}
                      </span>
                    );
                  })}
                  {(c.trades || []).length === 0 && <span className="text-xs text-slate-500">—</span>}
                </div>
                <div className="mt-4 grid grid-cols-2 gap-3 border-t border-slate-200 pt-3">
                  <Stat label="Crew Size" value={`${c.crew_size} workers`} icon={<Users className="h-4 w-4 text-[#1e3a8a]" />} />
                  <Stat label="Experience" value={`${c.experience_years} years`} icon={<Calendar className="h-4 w-4 text-[#1e3a8a]" />} />
                </div>
                <div className="mt-4 flex items-center justify-between gap-3 rounded-lg border-2 border-dashed border-ncc-navy bg-ncc-tint px-4 py-3" data-testid="portal-workmen-widget">
                  <div className="min-w-0">
                    <div className="industrial-label text-[10px] text-ncc-navy">Workmen Roster</div>
                    <div className="mt-0.5 font-mono text-xs text-slate-700">
                      Registered: <b className="text-slate-900">{workmenCount}</b>
                      {" · "}Balance: <b className={`${Number(c.crew_size || 0) - workmenCount > 0 ? "text-amber-700" : Number(c.crew_size || 0) - workmenCount < 0 ? "text-red-700" : "text-emerald-700"}`}>{Number(c.crew_size || 0) - workmenCount}</b>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => { setEditingWorkmanId(null); setWorkmenOpen(true); }}
                    data-testid="portal-workmen-btn"
                    className="inline-flex items-center gap-2 rounded-md border-2 border-ncc-navy bg-ncc-gold px-3 py-2 industrial-label text-ncc-navy hover:-translate-y-0.5 hover:shadow-brutal-sm"
                  >
                    <UserPlus className="h-4 w-4" /> Register Workmen / Labour
                  </button>
                </div>

                {/* Inline registered-workmen list (image 2) */}
                <WorkmenList
                  workmen={workmen}
                  contractor={c}
                  siteMasters={siteMasters}
                  onDelete={async (w) => {
                    if (!window.confirm(`Delete workman "${w.name}"? This cannot be undone.`)) return;
                    try {
                      await axios.delete(`${API}/contractor-auth/workmen/${w.id}`, { headers: authHeader });
                      const r = await axios.get(`${API}/contractor-auth/workmen`, { headers: authHeader });
                      setWorkmen(r.data || []);
                      toast.success("Workman deleted");
                    } catch (err) {
                      toast.error(err?.response?.data?.detail || "Delete failed");
                    }
                  }}
                  emptyHint="No workmen registered yet. Click ‘Register Workmen / Labour’ to add the first one."
                />
              </Card>
            )}
            {isPayroll && (
              <Card title="Printables" icon={<Printer className="h-4 w-4 text-ncc-gold2" />}>
                {(() => {
                  // Print Screening / Form-XI / ID Card unlock ONLY after Pay-Roster approval.
                  const prs = contractor.pay_roster_status || "not_submitted";
                  const canPrint = prs === "approved";
                  const lockMsg =
                    prs === "pending" ? "Pending admin approval — printables unlock after Approval-Pay-Roster." :
                    prs === "rejected" ? "Pay-Roster rejected — resubmit the Medical / Induction form to re-queue." :
                    "Submit the Medical / Induction form first to unlock printables.";
                  const lockTitle = canPrint ? "" : lockMsg;
                  const lockedBtnCls = "cursor-not-allowed opacity-50 pointer-events-none";
                  return (
                    <>
                      <div className="flex flex-wrap gap-2" data-testid="portal-payroll-printables" data-pay-roster-status={prs}>
                        <button
                          type="button"
                          onClick={() => setPayrollScreenOpen(true)}
                          data-testid="portal-payroll-screen-form-btn"
                          className="inline-flex items-center gap-2 rounded-md border-2 border-ncc-navy bg-ncc-tint px-3 py-2 industrial-label text-ncc-navy transition-all hover:-translate-y-0.5 hover:shadow-brutal-sm"
                        >
                          <ClipboardEdit className="h-4 w-4" />
                          Medical/Screening Form
                        </button>
                        <button
                          type="button"
                          onClick={handlePrintScreening}
                          disabled={!canPrint}
                          title={lockTitle}
                          data-testid="portal-payroll-print-screening"
                          className={`inline-flex items-center gap-2 rounded-md border-2 border-ncc-navy bg-white px-3 py-2 industrial-label text-ncc-navy transition-all hover:-translate-y-0.5 hover:shadow-brutal-sm ${canPrint ? "" : lockedBtnCls}`}
                        >
                          <Printer className="h-4 w-4" />
                          Print Screening Form
                        </button>
                        <button
                          type="button"
                          onClick={() => printFormXI(c, contractor)}
                          disabled={!canPrint}
                          title={lockTitle}
                          data-testid="portal-payroll-print-formxi"
                          className={`inline-flex items-center gap-2 rounded-md border-2 border-emerald-700 bg-white px-3 py-2 industrial-label text-emerald-800 transition-all hover:-translate-y-0.5 hover:shadow-brutal-sm ${canPrint ? "" : lockedBtnCls}`}
                        >
                          <Printer className="h-4 w-4" />
                          Medical Report (Form-XI)
                        </button>
                        <button
                          type="button"
                          onClick={handlePrintIdCard}
                          disabled={!canPrint}
                          title={lockTitle}
                          data-testid="portal-payroll-print-id-card"
                          className={`inline-flex items-center gap-2 rounded-md border-2 border-ncc-navy bg-ncc-gold px-3 py-2 industrial-label text-ncc-navy transition-all hover:-translate-y-0.5 hover:shadow-brutal-sm ${canPrint ? "" : lockedBtnCls}`}
                        >
                          <CreditCard className="h-4 w-4" />
                          Print Identity Card
                        </button>
                      </div>
                      {/* Lock / status banner */}
                      {!canPrint && (
                        <div
                          className={`mt-2 inline-flex items-center gap-1.5 rounded-md border px-2 py-1 font-mono text-[11px] ${
                            prs === "pending" ? "border-amber-300 bg-amber-50 text-amber-800" :
                            prs === "rejected" ? "border-red-300 bg-red-50 text-red-700" :
                            "border-slate-300 bg-slate-50 text-slate-600"
                          }`}
                          data-testid="portal-payroll-print-lock"
                        >
                          <Clock className="h-3 w-3" />
                          <span>
                            {prs === "pending" && <>Pay-Roster Status: <b>PENDING</b> · Awaiting admin approval to unlock printables.</>}
                            {prs === "rejected" && <>Pay-Roster Status: <b>REJECTED</b>{contractor.pay_roster_rejection_reason ? <> · {contractor.pay_roster_rejection_reason}</> : null} · Re-submit the Medical / Induction form.</>}
                            {(prs === "not_submitted" || (prs !== "pending" && prs !== "rejected")) && <>Submit the <b>Medical / Induction form</b> to start the Pay-Roster approval.</>}
                          </span>
                        </div>
                      )}
                      {canPrint && (
                        <div className="mt-2 inline-flex items-center gap-1.5 rounded-md border border-emerald-300 bg-emerald-50 px-2 py-1 font-mono text-[11px] text-emerald-700" data-testid="portal-payroll-print-unlocked">
                          <ShieldCheck className="h-3 w-3" />
                          Pay-Roster Status: <b>APPROVED</b> · Printables unlocked.
                        </div>
                      )}
                    </>
                  );
                })()}
                {!contractor.ncc_site_name && (
                  <div className="mt-2 font-mono text-[11px] text-amber-700" data-testid="portal-payroll-print-warn">
                    Tip: Identity Card needs an assigned NCC Site to generate the ID No.
                  </div>
                )}
                {contractor.id_card_no && (
                  <div className="mt-2 font-mono text-[11px] text-slate-600" data-testid="portal-payroll-id-no">
                    ID No.: <b className="text-ncc-navy">{contractor.id_card_no}</b>
                  </div>
                )}
              </Card>
            )}
            {isPayroll && (
              <Card title="Digital ID Card" icon={<CreditCard className="h-4 w-4 text-ncc-gold2" />}>
                <DigitalIdCard
                  testid="payroll-digital-id"
                  d={{
                    id: c.id,
                    name: c.name,
                    idNo: c.id_card_no,
                    trade: (c.trades && c.trades[0]) || c.contractor_type,
                    mobile: c.contact_phone,
                    site: c.ncc_site_name,
                    uan: c.uan_no,
                    bloodGroup: c.blood_group,
                    photo: photoSrc(c.photo),
                    active: c.pay_roster_status === "approved" && !c.withdrawn,
                    status: c.pay_roster_status || c.status,
                    issuedAt: c.id_card_issued_at,
                    cardTitle: "PAYROLL IDENTITY CARD",
                  }}
                />
                {!c.id_card_no && (
                  <p className="mt-3 text-center font-mono text-[11px] text-amber-700">ID No. not issued yet — use "Print Identity Card" above once approved, or contact admin.</p>
                )}
              </Card>
            )}
            {isPayroll && (
              <Card title="Experience" icon={<Calendar className="h-4 w-4 text-ncc-gold2" />}>
                <div className="grid grid-cols-2 gap-3">
                  <Stat label="India" value={`${c.experience_india_years || 0} years`} />
                  <Stat label="Abroad" value={`${c.experience_abroad_years || 0} years`} />
                </div>
              </Card>
            )}

            <Card title="ID Proof" icon={<ShieldCheck className="h-4 w-4 text-ncc-gold2" />}>
              <div className="grid grid-cols-2 gap-3">
                <Stat label="Type" value={c.id_proof_type || "—"} />
                <Stat label="Number" value={c.id_proof_number ? maskId(c.id_proof_number, c.id_proof_type) : "—"} mono />
              </div>
              {(c.id_docs || []).length > 0 && (
                <div className="mt-4 border-t border-slate-200 pt-3">
                  <div className="text-xs font-semibold tracking-wider text-slate-500">DOCUMENTS</div>
                  <div className="mt-2 space-y-1">
                    {c.id_docs.map((d, i) => (
                      <a key={i} href={d.url} target="_blank" rel="noreferrer" className="flex items-center gap-2 text-sm text-[#1e3a8a] hover:underline">
                        <FileText className="h-4 w-4" /> {d.name || `Document ${i + 1}`}
                      </a>
                    ))}
                  </div>
                </div>
              )}
            </Card>

            {c.bio && (
              <Card title="About" icon={<Briefcase className="h-4 w-4 text-ncc-gold2" />}>
                <p className="text-sm leading-relaxed text-slate-700 whitespace-pre-line">{c.bio}</p>
              </Card>
            )}
          </div>
        </div>
      </main>
      {!isPayroll && (
        <WorkmenDrawer
          open={workmenOpen}
          onClose={() => { setWorkmenOpen(false); setEditingWorkmanId(null); }}
          mode="self"
          contractorName={c.name}
          crewSize={c.crew_size}
          authToken={token}
          editingWorkmanId={editingWorkmanId}
          onChanged={async () => {
            try {
              const r = await axios.get(`${API}/contractor-auth/workmen`, { headers: authHeader });
              setWorkmen(r.data || []);
            } catch { /* ignore */ }
          }}
        />
      )}
      {isPayroll && (
        <PayrollScreenFormDrawer
          open={payrollScreenOpen}
          contractor={contractor}
          authToken={token}
          onClose={() => setPayrollScreenOpen(false)}
          onSaved={(updated) => { if (updated) setContractor((prev) => ({ ...prev, ...updated })); }}
        />
      )}
    </div>
  );
}

function maskId(num, type) {
  const s = String(num);
  if (type === "Aadhar") return `XXXX XXXX ${s.slice(-4)}`;
  return s.slice(0, 4) + "X".repeat(Math.max(0, s.length - 6)) + s.slice(-2);
}

function Row({ icon, value }) {
  return (
    <div className="flex items-center gap-2">
      {icon}
      <span className="font-mono">{value}</span>
    </div>
  );
}

function Card({ title, icon, children }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
      <div className="mb-3 flex items-center gap-2">
        {icon}
        <h2 className="font-heading text-sm font-bold tracking-[0.15em] text-[#1e3a8a]">{title.toUpperCase()}</h2>
      </div>
      {children}
    </div>
  );
}

function Stat({ label, value, icon, mono }) {
  return (
    <div>
      <div className="text-[10px] font-semibold tracking-wider text-slate-500">{label.toUpperCase()}</div>
      <div className={`mt-0.5 flex items-center gap-1 ${mono ? "font-mono" : ""} font-bold text-slate-900`}>
        {icon}
        <span>{value}</span>
      </div>
    </div>
  );
}

